"use client"

import { useState, useEffect, useCallback, useRef } from "react"
import { Search } from "lucide-react"

/**
 * Debounced search input component
 * @param {Object} props - Component props
 * @param {string} props.value - Search value
 * @param {Function} props.onChange - Change handler
 * @param {number} props.debounce - Debounce delay in ms
 */
const SearchBar = ({ value, onChange, debounce = 300 }) => {
  const [inputValue, setInputValue] = useState(value)

  // Debounced onChange callback
  const timeoutRef = useRef(null)
  const debouncedOnChange = useCallback(
    (newValue) => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
      timeoutRef.current = setTimeout(() => {
        onChange(newValue)
      }, debounce)
    },
    [onChange, debounce]
  )

  // Clean up the timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [])

  // Update local state when prop value changes
  useEffect(() => {
    setInputValue(value)
  }, [value])

  const handleChange = (e) => {
    const newValue = e.target.value
    setInputValue(newValue)
    debouncedOnChange(newValue)
  }

  return (
    <div className="relative w-full max-w-md">
      <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
        <Search className="h-5 w-5 text-gray-400" />
      </div>
      <input
        type="text"
        className="bg-gray-800 border border-gray-700 text-white text-sm rounded-lg focus:ring-blue-600 focus:border-blue-600 block w-full pl-10 p-2.5"
        placeholder="Search code, description, category..."
        value={inputValue}
        onChange={handleChange}
      />
    </div>
  )
}

export default SearchBar
